import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Phone } from 'lucide-react';

const SpecialRequests = ({ notes, onNotesChange, vendor }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);

  // Time in milliseconds to consider silence before stopping
  const SILENCE_TIMEOUT = 1000;

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.error('Speech recognition not supported in this browser');
      return;
    }

    const recognitionInstance = new SpeechRecognition();
    recognitionInstance.continuous = true;
    recognitionInstance.interimResults = true; // Enable interim results for real-time display
    recognitionInstance.maxAlternatives = 1;
    recognitionInstance.lang = 'en-US';

    recognitionInstance.onstart = () => {
      console.log('Recognition started');
      setIsRecording(true);
      setIsProcessing(false);
      setInterimTranscript('');
    };

    recognitionInstance.onend = () => {
      console.log('Recognition ended');
      setIsRecording(false);
      setIsProcessing(false);
      setInterimTranscript('');

      // Clear any existing silence timer
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    };

    recognitionInstance.onerror = (event) => {
      console.error('Speech recognition error', event);

      if (event.error === 'no-speech') {
        console.log('No speech detected');
        // Do not stop for no-speech errors, let the silence detection handle it
        return;
      }

      setIsRecording(false);
      setIsProcessing(false);
      setInterimTranscript('');

      // Clear silence timer on error
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    };

    recognitionInstance.onresult = (event) => {
      // Reset silence timer on new speech
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }

      // Set a new silence timer
      silenceTimerRef.current = window.setTimeout(() => {
        console.log('Silence detected, stopping recording');
        stopRecording();
      }, SILENCE_TIMEOUT);

      // Process results
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Update the interim transcript for real-time display
      setInterimTranscript(interimTranscript);

      // Update the final transcript if we have one
      if (finalTranscript) {
        onNotesChange(notes ? `${notes} ${finalTranscript}` : finalTranscript);
      }
    };

    recognitionRef.current = recognitionInstance;

    // Cleanup
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (error) {
          console.error('Error aborting recognition:', error);
        }
      }

      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    };
  }, [notes, onNotesChange]);

  // const startRecording = async () => {
  //   if (!recognitionRef.current) {
  //     console.error('Speech recognition is not supported');
  //     return;
  //   }

  //   console.log('Starting speech recognition');
  //   setIsProcessing(true);

  //   try {
  //     // Request microphone permission
  //     await navigator.mediaDevices.getUserMedia({ audio: true });

  //     // Add a small delay before starting recognition
  //     setTimeout(() => {
  //       try {
  //         recognitionRef.current.start();
  //         console.log('Recognition started successfully');
  //       } catch (error) {
  //         console.error('Error starting recognition:', error);
  //         setIsProcessing(false);
  //         setIsRecording(false);
  //       }
  //     }, 50);
  //   } catch (error) {
  //     console.error('Error accessing microphone:', error);
  //     setIsProcessing(false);
  //   }
  // };

  const stopRecording = () => {
    if (!recognitionRef.current || !isRecording) {
      return;
    }

    console.log('Stopping speech recognition');
    try {
      recognitionRef.current.stop();
    } catch (error) {
      console.error('Error stopping recognition:', error);
    }

    // Clear silence timer
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  // const toggleRecording = () => {
  //   if (isRecording) {
  //     stopRecording();
  //   } else {
  //     startRecording();
  //   }
  // };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 bg-white rounded-lg shadow-sm"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
          <Avatar className="w-12 h-12 border-2 border-white shadow-md flex-shrink-0">
            <AvatarImage src={vendor?.profileImage} alt={vendor?.shopName} />
            <AvatarFallback className="bg-green-100 text-green-800 text-xl">A</AvatarFallback>
          </Avatar>
        </div>

        <div className="flex-1">
          <h3 className="font-medium text-lg flex items-center justify-between">
            May I Help You?{' '}
            <Badge
              onClick={() => window.open(`tel:${vendor.contactNo}`)}
              variant="outline"
              className="bg-gray-50 cursor-pointer w-fit mt-1 text-gray-700 border-gray-200 flex items-center gap-1"
            >
              <Phone className="h-4 w-4 mr-1 text-green-500" />
              Call me
            </Badge>
          </h3>
          <p className="text-gray-600 text-sm mb-3">Have any Special Requests, let me know</p>

          <div className="relative">
            <input
              type="text"
              value={
                isRecording ? `${notes}${interimTranscript ? ` ${interimTranscript}` : ''}` : notes
              }
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder='For eg. "Give ripe papaya"'
              className="w-full p-3 pr-20 border rounded-lg focus:outline-none focus:border-purple-400"
              readOnly={isRecording}
            />
            {/* <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2">
              <button
                className={`p-2 hover:bg-gray-100 rounded-full relative ${isRecording ? 'bg-red-50' : ''}`}
                onClick={toggleRecording}
                disabled={isProcessing}
                aria-label={isRecording ? 'Stop recording' : 'Start recording'}
              >
                {isProcessing ? (
                  <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                ) : isRecording ? (
                  <Mic className="w-5 h-5 text-red-500" />
                ) : (
                  <Mic className="w-5 h-5 text-purple-600" />
                )}

                {isRecording && (
                  <motion.span
                    className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"
                    animate={{ opacity: [1, 0.5, 1] }}
                    transition={{ repeat: Number.POSITIVE_INFINITY, duration: 1.5 }}
                  />
                )}
              </button>
            </div> */}
          </div>

          {isRecording && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xs text-red-500 mt-2 flex items-center"
            >
              <span className="mr-1">Recording...</span>
              <span className="text-xs">
                Speak now to add your request (will stop after 2s of silence)
              </span>
            </motion.p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default SpecialRequests;
